/**
 * Encoding contract for feedback quotes in chat messages. A quote travels
 * inside the plain message content as markdown-blockquote lines, so it
 * round-trips through persistence without any schema change — both the live
 * bubble and replayed history derive the styled quote from raw content alone.
 */

// Long selections bloat the prompt without adding context; the chip clamps
// visually anyway, so cap the payload too.
const MAX_QUOTE_CHARS = 1200

/** Flattens a DOM selection (possibly spanning several feedback cards) into
 *  clean lines: trims each line, drops empty ones, caps the length. */
export function normalizeSelection(text: string): string {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  return lines.join("\n").slice(0, MAX_QUOTE_CHARS)
}

export function composeQuotedMessage(quote: string, question: string): string {
  const quoted = quote
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n")
  return `${quoted}\n\n${question}`
}

/**
 * Parses a leading blockquote out of raw message content. Returns null when
 * there is no quote or no question after it — then the content renders
 * literally, so a manually typed lone "> foo" degrades gracefully.
 */
export function splitLeadingQuote(
  content: string,
): { quote: string; question: string } | null {
  const lines = content.split("\n")
  const quoteLines: string[] = []
  let i = 0
  while (i < lines.length && /^> ?/.test(lines[i])) {
    quoteLines.push(lines[i].replace(/^> ?/, ""))
    i++
  }
  if (quoteLines.length === 0) return null
  const question = lines.slice(i).join("\n").trim()
  if (!question) return null
  return { quote: quoteLines.join("\n"), question }
}
